// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState, xHeaderActions } from "@bg/App/Store/Reducers";
import { screen } from "@bg/XDL/Helpers";
import { xScreen } from "@bg/XDL/Utilities";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Page, { PageProps as ContainerProps } from "@insite/mobius/Page";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { FC, useEffect, useState } from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

export interface LogoLinksSearchStyles {
    header: InjectableCss;
    placeholder: InjectableCss;
    container?: ContainerProps;
    logoWrapper: InjectableCss;
    ctaWrapper: InjectableCss;
    searchWrapper?: InjectableCss;
    containerContentWrapper?: InjectableCss;
    searchClickable?: ClickablePresentationProps;
    ctasWrapper?: InjectableCss;
}

const HeaderSize: any = {
    default: 102,
    sticky: 90,
    mobile: 141,
    top: 0,
    gsm: 0, // global message
};

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    header: {
        css: css`
            height: ${HeaderSize.default}px;
            position: absolute;
            top: ${HeaderSize.top}px;
            width: 100%;
            background-color: ${getColor("text.main")};
            transition: all 350ms ease-in;

            > div {
                height: 100%;
                width: 100%;
            }

            ${screen("lte", "md")`
                position: fixed;
                top:0;
                height: ${HeaderSize.mobile}px;
            `}

            ${({ globalMessage }: any) => globalMessage && css``}
            ${({ out }: any) =>
                out &&
                `
                    top: -${HeaderSize.default}px;

                `}
            ${({ sticky }: any) =>
                sticky &&
                css`
                    position: fixed;
                    top: 0;
                    height: ${HeaderSize.sticky}px;
                    z-index: 50;
                    transition: all 350ms ease-out 0s;
                `}
        `,
    },
    placeholder: {
        css: css`
            height: ${HeaderSize.default}px;
            ${({ sticky }: any) =>
                sticky &&
                css`
                    height: ${HeaderSize.sticky}px;
                    ${screen("lte", "md")`
                    height: ${HeaderSize.mobile}px;
                    `}
                `}
            ${({ expandHeight }: any) => expandHeight && `height: ${HeaderSize.default + 150}px;`}
        `,
    },
    containerContentWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            height: 100%;
            width: 100%;
            ${({ sticky }: any) => !sticky && ``}
            ${({ sticky }: any) => sticky && ``}
            ${screen("lte", "md")`
            flex-wrap: wrap;
            padding: 12px 0 20px 0;
            `}
        `,
    },
    logoWrapper: {
        css: css`
            text-align: center;
            height: 72px;
            width: 286px;
            min-width: 150px;
            margin-right: 20px;
            display: flex;
            align-items: center;
            justify-content: center;

            img {
                max-width: 100%;
                max-height: 100%;
                overflow: hidden;
            }

            ${({ sticky }: any) =>
                sticky &&
                css`
                    height: 50px;
                    width: 120px;
                    min-width: 120px;
                    margin-right: 20px;
                    order: 1;
                `}
            ${xScreen("lte", "md")} {
                height: 50px;
                width: 242px;
                min-width: 120px;
                margin-right: 10px;
                order: 1;
            }
            ${xScreen("lte", 420)} {
                width: 212px;
            }
            ${({ searchInputFocus }: any) =>
                searchInputFocus &&
                css`
                    ${xScreen("lte", 1025)} {
                        display: none;
                    }
                `};
        `,
    },
    ctaWrapper: {
        css: css`
            margin-right: 27px;
            ${({ sticky }: any) =>
                sticky &&
                `margin-right: 30px;
                @media (max-width: 359px) {
                    margin-right: 20px;
                }
            `};

            ${screen("lte", "md")`order: 2;`}
            &.cart-trigger {
                margin-right: 0;
            }
        `,
    },
    searchWrapper: {
        css: css`
            margin-right: 20px;
            width: 290px;
            height: 25px;
            max-width: 290px;
            transition: 0.2s;
            > div {
                display: flex;
                justify-content: center;
            }
            ${screen("lte", "md")`order: 3; margin:0;width:100%;max-width:100%;`};
            ${({ sticky }: any) => sticky && `width: 420px;max-width: 420px; order:2;`};
            ${({ searchInputFocus }: any) =>
                searchInputFocus &&
                `
                @media (min-width: 1025px) {
                    width:100%; max-width: 100%; display:flex; justify-content:center;margin-right: 0;
                }
                `};
        `,
    },
    ctasWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            ${({ sticky }: any) => sticky && `order:3;`};
            ${screen("lte", "md")`order: 2;`};
            ${({ searchInputFocus }: any) =>
                searchInputFocus &&
                `
            @media (min-width: 1025px) {
                display:none;
            }
            `};
        `,
    },
};

const mapStateToProps = (state: CustomAppState) => ({
    isSticky: state.xHeader.isSticky,
    searchInputFocus: state.xHeader.searchInputFocus,
    globalMessage: state.xHeader.globalMessage,
    permissions: state.context.permissions,
    pageType: getCurrentPage(state).type,
    session: state.context.session,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & DispatchProp<xHeaderActions>;

const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper & { sticky: boolean };
const isMobile = () => window.matchMedia("(max-width: 768px) and (hover: none)").matches;
const isTablet = () => window.matchMedia("(max-width: 1024px) and (hover: none)").matches;

const LogoLinksSearch: FC<Props> = ({
    id,
    dispatch,
    isSticky,
    searchInputFocus,
    globalMessage,
    permissions,
    pageType,
    session,
}) => {
    const placeholder = React.createRef<HTMLUListElement>();
    const [isHeaderOut, setHeaderOut] = useState(false);
    const [headerElement, setHeaderElement] = useState(undefined);

    const handleSticky = debounce(() => {
        if (headerElement) {
            const headerHeight = (headerElement as any).getBoundingClientRect().height;
            const top = window.scrollY;

            const newValue = top > headerHeight || window.innerWidth < 1025;
            if (newValue !== isSticky) {
                dispatch({ type: "xHeader/isSticky", isSticky: newValue });
            }

            const tempHeaderOut = top > headerHeight + (globalMessage ? HeaderSize.gsm : 0);
            if (tempHeaderOut !== isHeaderOut) {
                setHeaderOut(tempHeaderOut);
            }
        }
    }, 0);

    useEffect(() => {
        const headerElement = document.getElementById("header");
        setHeaderElement(headerElement as any);
        document.addEventListener("scroll", handleSticky, { passive: true });
        window.addEventListener("resize", handleSticky);
        handleSticky();
        return () => {
            document.removeEventListener("scroll", handleSticky);
            window.removeEventListener("resize", handleSticky);
        };
    });

    return (
        <StyledWrapper
            css={css`
                position: relative;
                width: 100%;
            `}
        >
            <StyledWrapperX
                className="placeholder"
                ref={placeholder}
                sticky={isSticky}
                globalMessage={globalMessage}
                expandHeight={permissions?.canAddWidget && pageType === "Header"}
                {...logoLinksSearchStyles.placeholder}
            />
            <StyledWrapperX
                sticky={isSticky}
                out={isHeaderOut}
                globalMessage={globalMessage}
                {...logoLinksSearchStyles.header}
            >
                <Page
                    {...logoLinksSearchStyles.container}
                    css={css`
                        width: 100%;
                        height: 100%;
                    `}
                >
                    <StyledWrapperX sticky={isSticky} {...logoLinksSearchStyles.containerContentWrapper}>
                        <StyledWrapperX
                            sticky={isSticky}
                            searchInputFocus={searchInputFocus}
                            {...logoLinksSearchStyles.searchWrapper}
                        >
                            <Zone contentId={id} zoneName="SearchZone" />
                        </StyledWrapperX>
                        <StyledWrapperX
                            sticky={isSticky}
                            searchInputFocus={searchInputFocus}
                            {...logoLinksSearchStyles.logoWrapper}
                        >
                            <Zone contentId={id} zoneName="LogoZone" />
                        </StyledWrapperX>
                        <StyledWrapperX
                            sticky={isSticky}
                            searchInputFocus={searchInputFocus}
                            {...logoLinksSearchStyles.ctasWrapper}
                        >
                            <StyledWrapperX
                                sticky={isSticky}
                                {...logoLinksSearchStyles.ctaWrapper}
                                className="my-account-trigger"
                            >
                                <Zone contentId={id} zoneName="MyAccountOverlayZone" />
                            </StyledWrapperX>
                            <StyledWrapperX
                                sticky={isSticky}
                                {...logoLinksSearchStyles.ctaWrapper}
                                className="cart-trigger"
                            >
                                <Zone contentId={id} zoneName="CartZone" />
                            </StyledWrapperX>
                        </StyledWrapperX>
                    </StyledWrapperX>
                </Page>
            </StyledWrapperX>
        </StyledWrapper>
    );
};

const logoLinksSearch: WidgetModule = {
    component: connect(mapStateToProps)(LogoLinksSearch),
    definition: {
        group: "XNGAGE" as any,
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
